import { Link } from "react-router-dom";
import { ArrowRight, Clock, Lock } from "lucide-react";
import { TOOLS } from "@/data/tools";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "@/components/Reveal";

const jump = (id) => (e) => { e.preventDefault(); scrollToId(id); };

const ToolkitOneCard = () => (
  <Reveal className="relative overflow-hidden rounded-[2rem] bg-brand p-7 text-brand-fg sm:p-10 lg:col-span-2 lg:row-span-2 lg:p-12" data-testid="toolkit-card-1">
    <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
      <div className="max-w-md">
        <p className="text-xs font-bold uppercase tracking-[0.22em] opacity-90">Toolkit 02 · Available now · Free</p>
        <h3 className="mt-4 font-display text-4xl font-medium leading-[1.02] sm:text-5xl">Free SENDCO Resource Pack</h3>
        <p className="mt-4 text-base leading-relaxed opacity-90 sm:text-lg">Practical resources to support SEND leadership, compliance and partnership working.</p>
      </div>
      <div className="h-36 w-36 shrink-0 overflow-hidden rounded-full border-4 border-sun sm:h-44 sm:w-44">
        <img src="/img/docs.jpg" alt="Papers and a checklist on a desk ready for review" className="h-full w-full object-cover" />
      </div>
    </div>
    <ol className="mt-10 grid gap-x-8 sm:grid-cols-2">
      {TOOLS.map((t) => (
        <li key={t.id} className="border-t border-current/20" style={{ borderColor: "color-mix(in srgb, currentColor 22%, transparent)" }}>
          <a href={`#${t.id}`} onClick={jump(t.id)} className="group flex items-center gap-3 py-3.5 font-bold" data-testid={`toolkit-1-link-${t.id}`}>
            <span className="font-display text-sm italic opacity-80">{t.n}</span>
            <span className="flex-1">{t.title}</span>
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
          </a>
        </li>
      ))}
    </ol>
    <a href="#toolkit-one" onClick={jump("toolkit-one")} className="btn btn-sun mt-8 text-base" data-testid="toolkit-1-open">
      Open the pack <ArrowRight size={18} aria-hidden />
    </a>
  </Reveal>
);

const Toolkit4Card = () => (
  <Reveal delay={0.16} data-testid="toolkit-card-4" className="group overflow-hidden rounded-[2rem] border-2 border-line bg-surface">
    <div className="aspect-[16/9] overflow-hidden"><img src="/img/meeting.jpg" alt="Two colleagues meeting to talk through notes" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
    <div className="p-7 sm:p-8">
      <p className="eyebrow">Toolkit 04 · Available now · Free</p>
      <h3 className="mt-3 font-display text-3xl font-medium">School Improvement &amp; Turnaround Planner</h3>
      <p className="mt-3 text-ink-soft">Whole-school improvement planning: turn Ofsted and area SEND findings into a 6- or 12-month plan with CPD tracks and governance sign-off.</p>
      <Link to="/toolkit-4" className="btn btn-brand btn-sm mt-6" data-testid="toolkit-4-open">Open Toolkit 4 <ArrowRight size={14} aria-hidden /></Link>
    </div>
  </Reveal>
);

const Toolkit3Card = () => (
  <Reveal delay={0.08} data-testid="toolkit-card-3" className="group overflow-hidden rounded-[2rem] border-2 border-line bg-surface">
    <div className="aspect-[16/9] overflow-hidden"><img src="/img/students.jpg" alt="Pupils working together in a classroom" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
    <div className="p-7 sm:p-8">
      <p className="eyebrow">Toolkit 03 · Available now · Free</p>
      <h3 className="mt-3 font-display text-3xl font-medium">SEND Master Operational Framework</h3>
      <p className="mt-3 text-ink-soft">Ten operational toolkits across governance and finance, teaching and interventions, and data, integration and people.</p>
      <Link to="/toolkit-3" className="btn btn-brand btn-sm mt-6" data-testid="toolkit-3-open">Open Toolkit 3 <ArrowRight size={14} aria-hidden /></Link>
    </div>
  </Reveal>
);

export const Toolkits = () => (
  <section id="toolkits" aria-labelledby="toolkits-title" className="py-24 lg:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">
      <Reveal className="mb-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] lg:items-end">
        <div>
          <p className="eyebrow mb-4">The toolkits</p>
          <h2 id="toolkits-title" className="font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Four toolkits.<br /><span className="italic text-brand">One honest start.</span>
          </h2>
        </div>
        <p className="text-base leading-relaxed text-ink-soft sm:text-lg">
          All four toolkits are free and ready to use today, with no sign-up, covering SEND and whole-school improvement planning. Start with the SENDCO Quick Start & Improvement Workbook, or print classroom support sheets.
        </p>
      </Reveal>
      <Reveal className="mb-5">
        <Link to="/workbook" data-testid="workbook-banner"
          className="group mb-5 flex flex-col gap-4 rounded-[2rem] bg-brand p-7 text-brand-fg sm:flex-row sm:items-center sm:justify-between sm:p-9">
          <span>
            <span className="block text-xs font-bold uppercase tracking-[0.22em] opacity-90">Free download · No login · v1.0</span>
            <span className="mt-2 block font-display text-3xl font-medium sm:text-4xl">SENDCO Quick Start &amp; Improvement Workbook</span>
            <span className="mt-1 block opacity-90">14 fill-in sections for reflection, review and improvement planning, from health check to 90-day plan.</span>
          </span>
          <span className="btn btn-sun shrink-0">Download workbook <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" aria-hidden /></span>
        </Link>
        <Link to="/quick-start" data-testid="quick-start-banner"
          className="group flex flex-col gap-4 rounded-[2rem] border-2 border-ink bg-sun p-7 text-sun-fg sm:flex-row sm:items-center sm:justify-between sm:p-9">
          <span>
            <span className="block text-xs font-bold uppercase tracking-[0.22em]">Toolkit 01 · Start here · Free · No sign-up</span>
            <span className="mt-2 block font-display text-3xl font-medium sm:text-4xl">Free SENDCO Quick Start Pack</span>
            <span className="mt-1 block">Practical SEND resources created to support busy schools. Fill it in online or download the PDF.</span>
          </span>
          <span className="btn shrink-0 bg-ink text-paper">Open the Quick Start Pack <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" aria-hidden /></span>
        </Link>
      </Reveal>
      <div className="grid gap-5 lg:grid-cols-3">
        <ToolkitOneCard />
        <Toolkit3Card />
        <Toolkit4Card />
        <Reveal className="grid overflow-hidden rounded-[2rem] border-2 border-line bg-surface sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:col-span-3" data-testid="resource-centre-card">
          <div className="aspect-[16/9] overflow-hidden sm:aspect-auto sm:h-full">
            <img src="/img/writing.jpg" alt="A person writing notes during a planning meeting" className="h-full w-full object-cover grayscale-[35%]" />
          </div>
          <div className="p-7 sm:p-8">
            <p className="eyebrow flex items-center gap-2"><Lock size={14} aria-hidden /> Optional · Now open</p>
            <h3 className="mt-3 font-display text-3xl font-medium">SENDCO Resource Centre</h3>
            <p className="mt-3 text-ink-soft">For schools that want a secure, private workspace: save progress, track actions, upload evidence and generate reports. The free toolkits stay open to everyone without registration.</p>
            <Link to="/resource-centre" className="btn btn-ghost btn-sm mt-6" data-testid="resource-centre-link">Find out more <ArrowRight size={14} aria-hidden /></Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
